import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    createComplaint,
    getComplaintEnums,
    getComplaintCategories,
    uploadComplaintAttachments,
} from '../../../redux/actions/SocietyPortal/ComplaintAction/ComplaintAction';
import { getEmployeesDropdown } from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { getFlatsWithOwnership } from '../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { getMemberFlats } from '../../../redux/actions/SocietyPortal/MemberPortalAction/MemberPortalAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import usePermissions from '../../../hook/usePermissions';
import { EMPLOYEE_ROSTER_PERMISSIONS, MEMBER_PORTAL_PERMISSIONS } from '../../../consts/permissions';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_COMPLAINTS } from '../../../Routing/routes';
import ComplaintForm from './ComplaintForm';

const AddComplaint = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        createComplaintLoading,
        complaintEnumsData,
        categoriesData,
    } = useSelector(state => state.ComplaintReducer || {});
    // The dedicated dropdown endpoint already returns an active-only,
    // dropdown-ready shape: { user_id, employee_id, first_name, last_name,
    // email, employee_type }. No filtering / remapping needed here.
    const { employeesDropdownData } = useSelector(state => state.EmployeeReducer || {});
    const { flatsWithOwnership } = useSelector(state => state.WingsAndFlatsReducers || {});
    const flats = useMemo(
        () => (Array.isArray(flatsWithOwnership) ? flatsWithOwnership : []),
        [flatsWithOwnership]
    );
    // The caller's own flats (owner or occupant), from the member API. A
    // resident raising from this screen gets their flat preselected so the
    // ticket lands on the right unit without them hunting through the list.
    // /api/member/flats needs any member_portal.* code, so only ask when the
    // caller holds one — staff and super admins get no prefill and no 403 toast.
    const { canAny } = usePermissions();
    const hasPortal = canAny(MEMBER_PORTAL_PERMISSIONS);
    const { flatsData: myFlatsData } = useSelector(state => state.MemberPortalReducer || {});
    const myPrimaryFlatId = useMemo(() => {
        const list = Array.isArray(myFlatsData) ? myFlatsData : [];
        return list.length > 0 ? list[0].flat_id : null;
    }, [myFlatsData]);
    // Memoised so ComplaintForm's initial-sync effect runs once when the flat
    // arrives, not on every render.
    const initial = useMemo(
        () => (hasPortal && myPrimaryFlatId ? { flat_id: myPrimaryFlatId } : undefined),
        [hasPortal, myPrimaryFlatId]
    );
    const assignableUsers = useMemo(
        () => (Array.isArray(employeesDropdownData) ? employeesDropdownData : []),
        [employeesDropdownData]
    );

    useEffect(() => {
        if (!complaintEnumsData) dispatch(getComplaintEnums());
    }, [dispatch, complaintEnumsData]);

    useEffect(() => {
        if (societyId) dispatch(getComplaintCategories({ societyId }));
    }, [dispatch, societyId]);
    useEffect(() => {
        // Dedicated endpoint returns the whole active roster in one shot. A
        // refused request would replace this page with Access Denied.
        if (societyId && canAny(EMPLOYEE_ROSTER_PERMISSIONS)) dispatch(getEmployeesDropdown({ societyId }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, societyId]);
    useEffect(() => {
        // Populates the flat + owner dropdown so complaints can be tied to a
        // specific unit without the user having to know its numeric ID.
        if (societyId) dispatch(getFlatsWithOwnership({ society_id: societyId }));
    }, [dispatch, societyId]);
    useEffect(() => {
        if (societyId && hasPortal) dispatch(getMemberFlats({ societyId }));
    }, [dispatch, societyId, hasPortal]);

    const handleSubmit = (payload, files = []) => {
        dispatch(createComplaint({
            society_id: societyId,
            ...payload,
            onSuccess: (res) => {
                const complaintId = res?.data?.complaint_id;
                // Kick off the attachment upload in the background so the user
                // isn't held on the form waiting for large files — the detail
                // page renders them once they land.
                if (complaintId && files.length > 0) {
                    dispatch(uploadComplaintAttachments({ id: complaintId, files }));
                }
                navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS);
            },
        }));
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-max-w-4xl tw-mx-auto">
                <button
                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS)}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800 tw-mb-3"
                >
                    <Icon icon="mdi:arrow-left" /> Back to Complaints
                </button>
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                    <div className="tw-p-4 tw-border-b tw-border-gray-100">
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800">New Complaint</h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Raise a new complaint on behalf of a resident</p>
                    </div>
                    <div className="tw-p-5">
                        <ComplaintForm
                            initial={initial}
                            enums={complaintEnumsData}
                            categories={categoriesData}
                            assignableUsers={assignableUsers}
                            flats={flats}
                            flatHint={initial ? 'Prefilled with your flat — change it if this is about another unit.' : null}
                            onSubmit={handleSubmit}
                            onCancel={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS)}
                            submitting={createComplaintLoading}
                            submitLabel="Create Complaint"
                            mode="create"
                            enableAttachments
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddComplaint;
